import React, { useEffect, useState, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import SearchIcon from 'components/svg/Search';
import CloseIcon from 'components/svg/Close';
import DefaultHeaderContext from '../DefaultHeaderContext';
import useBrowser from 'components/hooks/useBrowser';
import { breakpoints } from 'components/theme/breakpoints';
import { isTouch } from 'util/device';
import SearchConst from './Search.const';
import { createUseStyles } from 'react-jss';
import styles from './Search.styles';

let searchTimer = null;

const useStyles = createUseStyles(styles, { name: 'HeaderSearch' });
const Search = ({ isActive, onSetActive, query: searchQueryInitial, onSearch }) => {
  const classes = useStyles();
  const [isInFocus, setFocus] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchQueryInitial);
  const inputRef = useRef();

  const browser = useBrowser();
  const isSmall = browser.down(breakpoints.sm) || isTouch;

  let placeholder = SearchConst.SEARCH_SHORT;

  if (browser.up(breakpoints.md)) {
    placeholder = SearchConst.SEARCH_MOBILE;
  }

  if (browser.up(breakpoints.ml)) {
    placeholder = SearchConst.SEARCH_LONG;
  }

  // When only search icon is visible call toggle function to display full width search
  const { searchInFullWidth, toggleFullWidthSearch = () => {} } = useContext(DefaultHeaderContext);

  const onFocus = () => {
    setFocus(true);
    onSetActive(true);
  };
  const onBlur = () => setFocus(false);

  const onChange = (e) => {
    const query = e.target.value;
    if (searchTimer) {
      clearTimeout(searchTimer);
    }
    searchTimer = setTimeout(() => {
      if (!query && searchQuery) {
        onSearch('');
      }
      if (query !== searchQuery) {
        onSearch(query);
      }
      setSearchQuery(query);
    }, 500);
  };

  const clearSearch = () => {
    if (searchTimer) {
      clearTimeout(searchTimer);
    }
    setSearchQuery('');
    inputRef.current.value = '';
    onBlur();
    onSetActive(false);
    if (searchInFullWidth) {
      toggleFullWidthSearch();
    }
  };

  useEffect(() => {
    if (searchQueryInitial && inputRef.current) {
      inputRef.current.value = searchQueryInitial;
      onSearch(searchQuery);
    }
  }, [inputRef]);

  // Handle screen resize
  useEffect(() => {
    if (!isSmall && searchInFullWidth) {
      toggleFullWidthSearch();
      inputRef.current.value = searchQuery;
    }
    if (searchQuery && isSmall && !searchInFullWidth) {
      toggleFullWidthSearch();
      // Wait for input to be visible again and set current value
      const setInput = () => {
        if (!inputRef.current) {
          setTimeout(() => {
            setInput();
          }, 100);
          return;
        }
        inputRef.current.value = searchQuery;
      };
      setInput();
    }
  }, [isSmall]);

  const onSearchIconClick = () => {
    toggleFullWidthSearch();
    onSetActive(true);
  };

  if (isSmall && !searchInFullWidth) {
    return (
      <div className={classNames(classes.search, classes.searchRight)}>
        <div
          data-testid="search-search-icon-btn"
          onClick={onSearchIconClick}
          className={classNames(classes.searchIcon, classes.searchIconBtn)}
        >
          <SearchIcon />
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames(classes.search, {
        [classes.paddingLeft]: !searchInFullWidth,
      })}
    >
      <div
        className={classNames(classes.form, {
          [classes.formFocus]: isInFocus,
        })}
      >
        <div className={classes.searchIcon}>
          <SearchIcon />
        </div>
        <input
          data-testid="search-input-text"
          ref={inputRef}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          className={classes.input}
        />
        {(searchQuery || searchInFullWidth || isActive) && (
          <div data-testid="search-close-icon-btn" onClick={clearSearch}>
            <CloseIcon className={classes.closeIcon} />
          </div>
        )}
      </div>
    </div>
  );
};

Search.propTypes = {
  onSetActive: PropTypes.func.isRequired,
  isActive: PropTypes.bool.isRequired,
  onSearch: PropTypes.func.isRequired,
  query: PropTypes.string.isRequired,
};

export default Search;
